/**
 * Tamil TTS (MVP) using Web Speech API speechSynthesis.
 */

let ttsQueue: string[] = [];
let ttsAbort = false;

function getTamilVoice(): SpeechSynthesisVoice | null {
  if (typeof window === 'undefined' || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  return voices.find((v) => v.lang.startsWith('ta')) ?? voices[0] ?? null;
}

/**
 * Speak a single Tamil text. Uses Tamil voice if available.
 */
export function speakTamil(text: string): void {
  if (typeof window === 'undefined' || !window.speechSynthesis || !text?.trim()) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text.trim());
  u.lang = 'ta-IN';
  const voice = getTamilVoice();
  if (voice) u.voice = voice;
  window.speechSynthesis.speak(u);
}

/**
 * Stop any current and queued speech.
 */
export function stopSpeak(): void {
  ttsAbort = true;
  ttsQueue = [];
  if (typeof window !== 'undefined' && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
}

/**
 * Play a list of texts sequentially (for "Play All").
 * Waits for each utterance to end before playing the next.
 */
export function speakTamilSequence(
  texts: string[],
  onEnd?: () => void,
  delayBetweenMs = 400
): void {
  if (typeof window === 'undefined' || !window.speechSynthesis) {
    onEnd?.();
    return;
  }
  window.speechSynthesis.cancel();
  ttsAbort = false;
  const list = texts.map((t) => t?.trim()).filter(Boolean);
  if (list.length === 0) {
    onEnd?.();
    return;
  }
  let index = 0;
  const speakNext = () => {
    if (ttsAbort || index >= list.length) {
      onEnd?.();
      return;
    }
    const u = new SpeechSynthesisUtterance(list[index]);
    u.lang = 'ta-IN';
    const voice = getTamilVoice();
    if (voice) u.voice = voice;
    u.onend = () => {
      index += 1;
      setTimeout(speakNext, delayBetweenMs);
    };
    u.onerror = () => {
      index += 1;
      setTimeout(speakNext, delayBetweenMs);
    };
    window.speechSynthesis.speak(u);
  };
  speakNext();
}
